import { useState, useCallback, useRef } from "react";
import { FileUploadState } from "datasets/pages/tables/components/file-validation/types";
import { resolveObjectKey, encodeObjectKey } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import useUser from "~/contexts/user";

const { VITE_RWB_API } = import.meta.env;
const MULTIPART_LIMIT = 50 * 1024 * 1024; // 50 MB
const VALIDATION_UPLOAD_FOLDER = "ingestions/uploads"; // Folder for ingestion file uploads

interface RwbFileInfo {
  key: string;
  type: string;
  size?: number;
  s3_uri?: string;
}

interface RwbBrowseResponse {
  current_file?: RwbFileInfo;
  children?: RwbFileInfo[];
}

interface UploadPart {
  etag: string;
  part_num: number;
}

/**
 * Splits a file into chunks for multipart upload
 */
const splitFileIntoChunks = (file: File): Blob[] => {
  const maxChunks = 10000;
  const minChunkSize = 15 * 1024 * 1024; // 15 MB
  const chunkSize = Math.max(minChunkSize, file.size / maxChunks);
  const chunks: Blob[] = [];
  for (let start = 0; start < file.size; start += chunkSize) {
    const end = Math.min(start + chunkSize, file.size);
    chunks.push(file.slice(start, end));
  }
  return chunks;
};

export const useFileUpload = () => {
  const user = useUser();
  const [uploadState, setUploadState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
  });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Uploads a single chunk and tracks progress
   */
  const uploadChunk = useCallback(
    (
      url: string,
      chunk: Blob,
      file: File,
      onProgress: (loaded: number) => void,
      retry = { attemptsLeft: 3, delay: 1500 }
    ): Promise<string> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", file.type);

        let prevLoaded = 0;

        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) {
            return;
          }
          const delta = e.loaded - prevLoaded;
          onProgress(delta);
          prevLoaded = e.loaded;
        };

        const handleRetry = () =>
          setTimeout(() => {
            uploadChunk(url, chunk, file, onProgress, {
              attemptsLeft: retry.attemptsLeft - 1,
              delay: retry.delay * 2,
            })
              .then(resolve)
              .catch(reject);
          }, retry.delay);

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.getResponseHeader("ETag") || "");
          } else if (retry.attemptsLeft > 0) {
            handleRetry();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          if (retry.attemptsLeft > 0) {
            handleRetry();
          } else {
            reject(new Error("Network error"));
          }
        };

        abortControllerRef.current?.signal.addEventListener("abort", () => {
          xhr.abort();
        });
        xhr.send(chunk);
      });
    },
    []
  );

  /**
   * Uploads a file using multipart upload (for files > 50MB)
   */
  const uploadMultipartFile = useCallback(
    async (file: File, key: string): Promise<void> => {
      const maxParallelReq = 6;
      const chunks = splitFileIntoChunks(file);
      const parts: UploadPart[] = [];

      // Start multipart upload
      const startResp = await fetch(`${VITE_RWB_API}/file-browser/start-multipart-upload`, {
        method: "POST",
        signal: abortControllerRef.current?.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, file_type: file.type }),
      });

      if (!startResp.ok) {
        throw new Error("Unable to initiate multipart upload");
      }

      const uploadId = await startResp.json();

      // Upload chunks in parallel batches
      for (let startPartNum = 1; startPartNum <= chunks.length; startPartNum += maxParallelReq) {
        const endPartNum = Math.min(startPartNum + maxParallelReq - 1, chunks.length);

        // Get pre-signed multipart upload URLs
        const presignedResp = await fetch(`${VITE_RWB_API}/file-browser/generate-multipart-urls`, {
          method: "POST",
          signal: abortControllerRef.current?.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            key,
            upload_id: uploadId,
            start: startPartNum,
            end: endPartNum,
          }),
        });

        if (!presignedResp.ok) {
          throw new Error("Unable to generate pre-signed URLs");
        }

        const urls: string[] = await presignedResp.json();

        // Upload chunks in parallel
        await Promise.all(
          urls.map(async (url, idx) => {
            const chunkIndex = startPartNum - 1 + idx;
            const etag = await uploadChunk(url, chunks[chunkIndex], file, (delta) => {
              setUploadState((prev) => {
                const currentProgress = prev.uploadProgress || 0;
                const newProgress = Math.min(
                  currentProgress + (delta / file.size) * 100,
                  99 // Don't set to 100% until complete
                );
                return { ...prev, uploadProgress: newProgress };
              });
            });
            parts.push({ etag, part_num: startPartNum + idx });
          })
        );
      }

      // Complete multipart upload
      const sortedParts = parts.sort((a, b) => a.part_num - b.part_num);
      const uniqueParts = Array.from(
        new Map(sortedParts.map((part) => [part.part_num, part])).values()
      );

      const completeResp = await fetch(`${VITE_RWB_API}/file-browser/complete-multipart-upload`, {
        method: "POST",
        signal: abortControllerRef.current?.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key,
          upload_id: uploadId,
          parts: uniqueParts,
        }),
      });

      if (!completeResp.ok) {
        throw new Error("Unable to complete upload");
      }

      setUploadState((prev) => ({
        ...prev,
        uploadProgress: 100,
      }));
    },
    [uploadChunk]
  );

  /**
   * Uploads a file using single-part upload (for files < 50MB)
   */
  const uploadSingleFile = useCallback(
    async (file: File, key: string): Promise<void> => {
      const presignedResp = await fetch(`${VITE_RWB_API}/file-browser/generate-upload-url`, {
        method: "POST",
        signal: abortControllerRef.current?.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, file_type: file.type }),
      });

      if (!presignedResp.ok) {
        throw new Error("Unable to get upload URL");
      }

      const url = await presignedResp.json();
      await uploadChunk(url, file, file, (delta) => {
        setUploadState((prev) => {
          const currentProgress = prev.uploadProgress || 0;
          const newProgress = Math.min(currentProgress + (delta / file.size) * 100, 100);
          return { ...prev, uploadProgress: newProgress };
        });
      });
    },
    [uploadChunk]
  );

  /**
   * Upload a file from the user's device to their personal bucket
   * @param file - The file to upload
   * @returns FileInfo for the uploaded file
   */
  const uploadFile = useCallback(
    async (file: File): Promise<FileInfo> => {
      if (!user?.email) {
        throw new Error("User email is required for upload");
      }

      // Cancel any existing upload
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setUploadState((prev) => ({ ...prev, isUploading: true, uploadProgress: 0 }));
      setUploadError(null);

      try {
        // Construct the upload path: scratch/users/{email}/ingestions/uploads/{filename}
        const personalPath = resolveObjectKey(BaseDirectory.PERSONAL, user.email);
        const uploadKey = `${personalPath}/${VALIDATION_UPLOAD_FOLDER}/${file.name}`;
        const parentKey = `${personalPath}/${VALIDATION_UPLOAD_FOLDER}/`;

        // Choose upload method based on file size
        const uploadFn = file.size < MULTIPART_LIMIT ? uploadSingleFile : uploadMultipartFile;
        await uploadFn(file, uploadKey);

        // Fetch file info from RWB to get the s3_uri
        // The browse endpoint returns files with s3_uri populated
        let s3Uri: string | undefined;
        try {
          const browseResp = await fetch(
            `${VITE_RWB_API}/file-browser/${encodeObjectKey(parentKey)}`
          );
          if (browseResp.ok) {
            const browseData: RwbBrowseResponse = await browseResp.json();
            // Find our uploaded file in the children
            const uploadedFile = browseData.children?.find((f) => f.key === uploadKey);
            s3Uri = uploadedFile?.s3_uri;
          }
        } catch {
          // If we can't get the s3_uri, continue without it
          // The validation will fail but we'll have a better error message
          console.warn("Could not fetch s3_uri for uploaded file");
        }

        // Create FileInfo for the uploaded file
        const uploadedFileInfo: FileInfo = {
          key: uploadKey,
          type: FileType.FILE,
          size: file.size,
          s3_uri: s3Uri,
        };

        setUploadState((prev) => ({
          ...prev,
          selectedFile: uploadedFileInfo,
          isUploading: false,
          uploadProgress: 100,
        }));

        return uploadedFileInfo;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Upload failed";
        setUploadError(errorMessage);
        setUploadState((prev) => ({ ...prev, isUploading: false, uploadProgress: 0 }));
        throw error;
      } finally {
        abortControllerRef.current = null;
      }
    },
    [user?.email, uploadSingleFile, uploadMultipartFile]
  );

  /**
   * Cancel the current upload
   */
  const cancelUpload = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setUploadState((prev) => ({ ...prev, isUploading: false, uploadProgress: 0 }));
  }, []);

  /**
   * Select an existing file from cloud storage.
   * Accepts a FileInfo object directly from the FileTree.
   */
  const selectFromStorage = useCallback((file: FileInfo): void => {
    setUploadError(null);
    setUploadState((prev) => ({ ...prev, selectedFile: file }));
  }, []);

  /**
   * Reset the upload state to initial values
   */
  const resetUpload = useCallback(() => {
    setUploadState({
      selectedFile: null,
      isUploading: false,
      uploadProgress: undefined,
    });
    setUploadError(null);
  }, []);

  return {
    uploadState,
    uploadFile,
    selectFromStorage,
    resetUpload,
    uploadError,
    cancelUpload,
  };
};
